import { useSyncExternalStore } from 'react';

/**
 * Installing Pulse as an app (PWA) — see public/manifest.webmanifest. Two very different paths:
 *   - Chromium (Android, desktop) fires `beforeinstallprompt`, which we hold on to so our own
 *     Install button can open the browser's install dialog later.
 *   - iOS has no install API at all: the user adds the site from Safari's Share menu. It matters
 *     more there, because iOS only allows web push for a site installed to the Home Screen.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** Called once from main.tsx before render: the event fires early in page load, once, and is
 * lost if nothing is listening yet. Suppressing it hides Chrome's own mini-infobar in favour of
 * the in-app banner (InstallBanner); the browser's address-bar install icon still works. */
export function captureInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    emit();
  });
}

/** Running as the installed app rather than in a browser tab. */
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** iPhone/iPad, including iPadOS, which reports itself as a Mac but has touch. */
export function isIOS(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function subscribe(onChange: () => void) {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

/** Whether the browser's own install dialog can be opened right now (Chromium only). */
export function useCanPromptInstall(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => deferredPrompt !== null,
    () => false,
  );
}

/** Opens the browser's install dialog. Resolves true if the user installed. A prompt can only be
 * used once, so it's dropped either way. */
export async function promptInstall(): Promise<boolean> {
  const prompt = deferredPrompt;
  if (!prompt) return false;
  deferredPrompt = null;
  emit();
  await prompt.prompt();
  const { outcome } = await prompt.userChoice;
  return outcome === 'accepted';
}

/** Per-device "don't show this banner again" flags. */
export function isBannerDismissed(key: string): boolean {
  try {
    return localStorage.getItem(`pulse:banner-dismissed:${key}`) === 'true';
  } catch {
    return false;
  }
}

export function dismissBanner(key: string): void {
  try {
    localStorage.setItem(`pulse:banner-dismissed:${key}`, 'true');
  } catch {
    // Private browsing etc. — the banner just comes back next visit.
  }
}
